// SPDX-License-Identifier: AGPL-3.0-or-later

import {VoiceActivityCard} from '@app/features/user/components/profile/VoiceActivityCard';
import styles from '@app/features/user/components/profile/VoiceActivitySection.module.css';
import {useUserVoiceActivityAggregates} from '@app/features/voice/hooks/useUserVoiceActivities';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

interface VoiceActivitySectionProps {
	userId: string;
	onNavigate?: () => void;
	showAllActivities?: boolean;
}
export const VoiceActivitySection: React.FC<VoiceActivitySectionProps> = observer(
	({userId, onNavigate, showAllActivities = false}) => {
		const {t} = useLingui();
		const activityAggregates = useUserVoiceActivityAggregates(userId);
		const primaryActivity = activityAggregates[0]?.primaryActivity;
		const aggregatedActivities = useMemo(
			() => activityAggregates.map((aggregate) => aggregate.primaryActivity),
			[activityAggregates],
		);
		const additionalCount = Math.max(0, aggregatedActivities.length - 1);
		const additionalCallsLabel = useMemo(() => {
			if (additionalCount === 1) {
				return t`And 1 other call`;
			}
			return t`And ${additionalCount} other calls`;
		}, [additionalCount, t]);
		if (!primaryActivity) {
			return null;
		}
		if (showAllActivities) {
			return (
				<div className={styles.section}>
					<div className={styles.allCallsGrid}>
						{aggregatedActivities.map((activity) => (
							<div key={`${activity.guildId ?? 'dm'}:${activity.channelId}`} className={styles.gridItem}>
								<VoiceActivityCard activity={activity} onNavigate={onNavigate} />
							</div>
						))}
					</div>
				</div>
			);
		}
		return (
			<div className={styles.section}>
				<VoiceActivityCard activity={primaryActivity} onNavigate={onNavigate} />
				{additionalCount > 0 ? <span className={styles.moreCallsText}>{additionalCallsLabel}</span> : null}
			</div>
		);
	},
);
